/**
 * "World Changer of the Day" home hero card — deterministic daily rotation
 * across the curated set. Encourages users to discover a new luminary each day.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Sparkles, ArrowUpRight } from "lucide-react";

export default function WorldChangerOfTheDay() {
  const [c, setC] = useState(null);
  const [failedImg, setFailedImg] = useState(false);

  useEffect(() => {
    http.get("/world-changers/daily").then((r) => setC(r.data)).catch(() => setC(null));
  }, []);

  if (!c) return null;
  const monogram = (c.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const years = [c.born?.slice(0, 4), c.died?.slice(0, 4) || "present"].filter(Boolean).join(" – ");

  return (
    <section
      data-testid="world-changer-of-the-day"
      className="w-full border-y"
      style={{ background: "var(--wofy-bg)", borderColor: "var(--wofy-border)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4 flex justify-center lg:justify-start">
            {failedImg || !c.photo_url ? (
              <div
                className="rounded-3xl grid place-items-center font-mono font-bold"
                style={{ width: 260, height: 260, background: "var(--wofy-surface)", color: "var(--wofy-primary)", fontSize: 88 }}
              >
                {monogram}
              </div>
            ) : (
              <img
                src={c.photo_url}
                alt={c.name}
                onError={() => setFailedImg(true)}
                className="rounded-3xl object-cover border"
                style={{ width: 260, height: 260, borderColor: "var(--wofy-border)" }}
              />
            )}
          </div>
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
              <div className="overline" data-testid="wcotd-overline">WORLD CHANGER OF THE DAY</div>
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight" data-testid="wcotd-name">
              {c.name}
            </h2>
            <div className="mt-2 text-sm font-mono uppercase tracking-wider opacity-70">
              {[c.nationality, years, c.occupation].filter(Boolean).join(" · ")}
            </div>
            {c.tagline && (
              <p className="mt-4 text-lg leading-relaxed max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
                {c.tagline}
              </p>
            )}
            {c.categories?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {c.categories.slice(0, 5).map((k) => (
                  <span key={k} className="text-[10px] font-mono uppercase tracking-wider px-3 py-1 rounded-full" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>
                    {k}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/world-changers/${c.slug || c.id}`}
                data-testid="wcotd-read-profile"
                className="wofy-btn-primary"
              >
                Read full profile <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/world-changers" data-testid="wcotd-explore-all" className="wofy-btn-ghost">
                Explore all World Changers
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
